import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const CommunicationPanel = ({ selectedPackage }) => {
  const [message, setMessage] = useState('');
  const [notificationPreferences, setNotificationPreferences] = useState({
    email: true,
    sms: false,
    push: true
  });

  const mockMessages = [
    {
      id: 1,
      sender: 'Équipe Production',
      message: 'Votre commande a été expédiée et est en route vers vous.',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      type: 'received'
    },
    {
      id: 2,
      sender: 'Vous',
      message: 'Merci pour la mise à jour. Quelle est l\'heure de livraison prévue ?',
      timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000),
      type: 'sent'
    },
    {
      id: 3,
      sender: 'Service Livraison',
      message: 'La livraison est prévue demain entre 9h et 12h. Vous recevrez une notification 30 minutes avant.',
      timestamp: new Date(Date.now() - 30 * 60 * 1000),
      type: 'received'
    }
  ];

  const handleSendMessage = (e) => {
    e?.preventDefault();
    if (message?.trim()) {
      // Send message logic here
      console.log('Sending message:', message);
      setMessage('');
    }
  };

  const handleNotificationToggle = (type) => {
    setNotificationPreferences(prev => ({
      ...prev,
      [type]: !prev?.[type]
    }));
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const diff = Math.floor((now - timestamp) / 1000);
    
    if (diff < 60) return 'À l\'instant';
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`;
    return timestamp?.toLocaleDateString('fr-FR');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Messages */}
      <div className="lg:col-span-2">
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-primary/5 border-b border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Messages</h3>
                {selectedPackage && (
                  <p className="text-sm text-gray-600">Colis: {selectedPackage?.packageNumber}</p>
                )}
              </div>
              <Button variant="outline" size="sm" iconName="Phone">
                Appeler
              </Button>
            </div>
          </div>

          {/* Message List */}
          <div className="p-4 space-y-4 max-h-[500px] overflow-y-auto">
            {mockMessages?.map(msg => (
              <div
                key={msg?.id}
                className={`flex ${msg?.type === 'sent' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[70%] rounded-lg p-4 ${
                    msg?.type === 'sent' ?'bg-primary text-white' :'bg-gray-100 text-gray-900'
                  }`}
                >
                  {msg?.type === 'received' && (
                    <div className="text-xs font-semibold mb-1 opacity-80">{msg?.sender}</div>
                  )}
                  <p className="text-sm">{msg?.message}</p>
                  <div className={`text-xs mt-2 ${
                    msg?.type === 'sent' ? 'text-white/70' : 'text-gray-600'
                  }`}>
                    {formatTime(msg?.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Message Input */}
          <div className="border-t border-gray-200 p-4">
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <Input
                type="text"
                placeholder="Tapez votre message..."
                value={message}
                onChange={(e) => setMessage(e?.target?.value)}
                className="flex-1"
              />
              <Button
                type="submit"
                variant="primary"
                iconName="Send"
                disabled={!message?.trim()}
              >
                Envoyer
              </Button>
            </form>
          </div>
        </div>
      </div>
      {/* Notification Preferences */}
      <div className="lg:col-span-1 space-y-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Préférences de notification</h3>
          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-3">
                <Icon name="Mail" size={20} className="text-gray-600" />
                <div>
                  <div className="font-medium text-gray-900">Email</div>
                  <div className="text-xs text-gray-600">Recevoir les mises à jour par email</div>
                </div>
              </div>
              <input
                type="checkbox"
                checked={notificationPreferences?.email}
                onChange={() => handleNotificationToggle('email')}
                className="w-5 h-5 text-primary rounded focus:ring-primary"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-3">
                <Icon name="Smartphone" size={20} className="text-gray-600" />
                <div>
                  <div className="font-medium text-gray-900">SMS</div>
                  <div className="text-xs text-gray-600">Recevoir les alertes par SMS</div>
                </div>
              </div>
              <input
                type="checkbox"
                checked={notificationPreferences?.sms}
                onChange={() => handleNotificationToggle('sms')}
                className="w-5 h-5 text-primary rounded focus:ring-primary"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-3">
                <Icon name="Bell" size={20} className="text-gray-600" />
                <div>
                  <div className="font-medium text-gray-900">Push</div>
                  <div className="text-xs text-gray-600">Notifications push sur l'app</div>
                </div>
              </div>
              <input
                type="checkbox"
                checked={notificationPreferences?.push}
                onChange={() => handleNotificationToggle('push')}
                className="w-5 h-5 text-primary rounded focus:ring-primary"
              />
            </label>
          </div>

          <Button variant="primary" className="w-full mt-6">
            Enregistrer les préférences
          </Button>
        </div>

        {/* Quick Actions */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions rapides</h3>
          <div className="space-y-2">
            <Button variant="outline" iconName="FileText" className="w-full justify-start">
              Télécharger facture
            </Button>
            <Button variant="outline" iconName="Download" className="w-full justify-start">
              Certificat de qualité
            </Button>
            <Button variant="outline" iconName="AlertCircle" className="w-full justify-start">
              Signaler un problème
            </Button>
            <Button variant="outline" iconName="HelpCircle" className="w-full justify-start">
              Centre d'aide
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunicationPanel;